import { useEffect } from 'react';
import { gql, useSuspenseQuery } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';

import { setFeatures } from '../../../actions/features';
import { getDeviceIDFromCookie } from '../../../common/device-cookie';
import { apolloContextWithApiV2ClientName } from '../../../graphql/constants';
import { getSessionCompanyId } from '../../../selectors/session';
import { FeatureFlag } from '../types';
import useCacheFeatureFlags from './useCacheFeatureFlags';
import useRefetchMeForV2Migration from './useRefetchMeForV2Migration';

const query = gql`
  query getEnabledFeatureFlags(
    $deviceId: String!
    $platform: String
    $companyId: String
    $featureFlagNames: [String!]
  ) {
    getEnabledFeatureFlags(
      data: {
        deviceId: $deviceId
        platform: $platform
        companyId: $companyId
        featureFlagNames: $featureFlagNames
      }
    ) {
      name
      variant {
        name
        type
        value
      }
    }
  }
`;

type QueryOutput = {
  getEnabledFeatureFlags: FeatureFlag[];
};

type QueryInput = {
  companyId: string | undefined;
  deviceId: string;
  platform: string | undefined;
  featureFlagNames: string[];
};
type Props = {
  fetchPolicy?: 'cache-first' | 'network-only';
};

export default function useGetFeatureFlags(props: Props = {}) {
  const { fetchPolicy = 'cache-first' } = props;
  const companyId: string | undefined = useSelector(getSessionCompanyId);
  const { data } = useSuspenseQuery<QueryOutput, QueryInput>(query, {
    variables: {
      deviceId: getDeviceIDFromCookie(),
      companyId: companyId,
      platform: 'EMP',
      featureFlagNames: [],
    },
    fetchPolicy,
    context: apolloContextWithApiV2ClientName,
  });
  const dispatch = useDispatch();
  const { setCache } = useCacheFeatureFlags();

  useEffect(() => {
    dispatch(setFeatures(data?.getEnabledFeatureFlags));
    setCache(data?.getEnabledFeatureFlags);
  }, [companyId, data?.getEnabledFeatureFlags, dispatch, setCache]);

  // Re-fetch /me with v2 if the empMigrateMeToV2 flag is enabled. This is
  // needed because the initial SessionInitializer fetchMe() ran before flags
  // were available and therefore defaulted to the v1 endpoint.
  useRefetchMeForV2Migration(data?.getEnabledFeatureFlags);

  return data?.getEnabledFeatureFlags;
}
